import React, { useMemo, useState } from "react";
import { FiX, FiCheckCircle, FiActivity, FiEdit2, FiMail } from "react-icons/fi";

/**
 * Roadmap progress dashboard.
 *
 * Shows where each item stands and lets the roadmap owner move it along.
 *
 * Three stages, and nothing finer. An earlier version had four stages plus a
 * five-step checklist per item that drove a percentage; review feedback was
 * that the detail implied a precision we can't honour. The stage IS the
 * update now — there is no percentage, no checklist and nothing to tick.
 *
 * Colour note: the stages are a funnel — an ORDINAL scale, not unordered
 * categories — so they use a single-hue ramp stepped light→dark rather than
 * three unrelated hues, anchored on the app's own accent. Stage identity is
 * never colour-alone: every mark carries a label.
 */
const STAGES = ["Exploring", "In Progress", "Delivery"];

const stageIndex = (status) => {
  const i = STAGES.indexOf(status);
  return i === -1 ? 0 : i;
};

const ProgressDashboardModal = ({
  open,
  onClose,
  items = [],
  isAdmin = false,
  owner = null,
  onUpdateItem,
  pendingRequests = [],
  onReviewRequest,
}) => {
  const [error, setError] = useState("");

  const [editing, setEditing] = useState(null); // itemId currently being edited
  const [draft, setDraft] = useState({ status: "", eta: "" });
  const [savingId, setSavingId] = useState(null);
  const [busyRequest, setBusyRequest] = useState(null);

  const counts = useMemo(() => {
    const c = STAGES.map(() => 0);
    for (const it of items) c[stageIndex(it.status)] += 1;
    return c;
  }, [items]);

  const total = items.length;

  if (!open) return null;

  const startEdit = (item) => {
    setEditing(item.id);
    setDraft({ status: item.status, eta: item.eta || "" });
  };

  const saveEdit = async (item) => {
    setSavingId(item.id);
    setError("");
    try {
      await onUpdateItem({
        itemId: item.id,
        status: draft.status,
        eta: draft.eta,
      });
      setEditing(null);
    } catch (err) {
      setError(err?.message || "Couldn't save the change.");
    } finally {
      setSavingId(null);
    }
  };

  const decideRequest = async (r, approve) => {
    setBusyRequest(r.id);
    setError("");
    try {
      await onReviewRequest({ itemId: r.id, approve });
    } catch (err) {
      setError(err?.message || "Couldn't record the decision.");
    } finally {
      setBusyRequest(null);
    }
  };

  return (
    <div className="rn-modal-overlay" onClick={onClose}>
      <div
        className="rn-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Roadmap progress"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rn-modal-head">
          <div className="rn-modal-title">
            <FiActivity size={18} />
            <div>
              <h2>Roadmap Progress</h2>
              <p>Where every item stands today.</p>
            </div>
          </div>
          <button
            type="button"
            className="rn-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="rn-modal-body">
          {/* Who keeps this current. Without it, a stage that hasn't moved in
              a month leaves nobody to ask. */}
          {owner?.name ? (
            <div className="rn-owner-note">
              <FiMail size={14} />
              <span>
                <strong>{owner.name}</strong> reviews new requests and updates
                these stages.
                {owner.email ? (
                  <>
                    {" "}
                    <a href={`mailto:${owner.email}`}>{owner.email}</a>
                  </>
                ) : null}
              </span>
            </div>
          ) : null}

          {/* ── Pipeline distribution ──────────────────────────────────
              One stacked bar showing how the roadmap spreads across the
              three stages, with a 2px surface gap between segments. */}
          {total > 0 && (
            <figure className="rn-chart">
              <figcaption className="rn-chart-caption">
                Pipeline — {total} item{total === 1 ? "" : "s"}
              </figcaption>

              <div
                className="rn-pipeline"
                role="img"
                aria-label={STAGES.map((s, i) => `${s}: ${counts[i]}`).join(", ")}
              >
                {STAGES.map((s, i) =>
                  counts[i] > 0 ? (
                    <div
                      key={s}
                      className={`rn-pipeline-seg seg-${i}`}
                      style={{ flexGrow: counts[i] }}
                      title={`${s}: ${counts[i]} of ${total}`}
                    />
                  ) : null,
                )}
              </div>

              {/* Legend — identity is never colour-alone. */}
              <ul className="rn-legend">
                {STAGES.map((s, i) => (
                  <li key={s} className="rn-legend-item">
                    <span className={`rn-legend-swatch seg-${i}`} />
                    <span className="rn-legend-label">{s}</span>
                    <span className="rn-legend-value">{counts[i]}</span>
                  </li>
                ))}
              </ul>
            </figure>
          )}

          {/* ── Requests awaiting review (owner only) ──
              Requests don't appear on the roadmap on submission, so they need
              somewhere to be seen and decided on. */}
          {isAdmin && pendingRequests.length > 0 && (
            <div className="rn-pending">
              <div className="rn-pending-head">
                {pendingRequests.length} request
                {pendingRequests.length === 1 ? "" : "s"} awaiting review
              </div>
              {pendingRequests.map((r) => (
                <div key={r.id} className="rn-pending-item">
                  <div className="rn-pending-main">
                    <div className="rn-pending-title">{r.title}</div>
                    {r.description ? (
                      <p className="rn-pending-desc">{r.description}</p>
                    ) : null}
                    <div className="rn-pending-meta">
                      {r.requestedBy ? (
                        <>Requested by {r.requestedBy}</>
                      ) : (
                        "Requested"
                      )}
                      {r.eta ? <> · wants it by {r.eta}</> : null}
                    </div>
                  </div>
                  <div className="rn-pending-actions">
                    <button
                      type="button"
                      className="rn-admin-save"
                      onClick={() => decideRequest(r, true)}
                      disabled={busyRequest === r.id}
                    >
                      {busyRequest === r.id ? "…" : "Approve"}
                    </button>
                    <button
                      type="button"
                      className="rn-admin-cancel"
                      onClick={() => decideRequest(r, false)}
                      disabled={busyRequest === r.id}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Per-item stage ─────────────────────────────────────── */}
          <div className="rn-progress-list">
            {items.length === 0 && (
              <div className="rn-empty">Nothing on the roadmap yet.</div>
            )}

            {items.map((it) => {
              const idx = stageIndex(it.status);
              const delivered = idx === STAGES.length - 1;
              const isEditing = editing === it.id;

              return (
                <div key={it.id} className="rn-progress-item">
                  <div className="rn-progress-item-head">
                    <span className="rn-progress-item-title">{it.title}</span>
                    <span className="rn-progress-item-meta">
                      <span className={`rn-progress-item-stage seg-${idx}`}>
                        {delivered ? <FiCheckCircle size={12} /> : null}{" "}
                        {it.status}
                      </span>
                      {isAdmin && !isEditing && (
                        <button
                          type="button"
                          className="rn-edit-btn"
                          onClick={() => startEdit(it)}
                          title="Update stage"
                        >
                          <FiEdit2 size={12} />
                        </button>
                      )}
                    </span>
                  </div>

                  {it.description ? (
                    <p className="rn-progress-item-desc">{it.description}</p>
                  ) : null}

                  {/* Where the item sits in the funnel — three marks, the
                      current one filled. No percentage: the stage is the
                      whole of what we're claiming. */}
                  <div
                    className="rn-stagebar"
                    role="img"
                    aria-label={`Stage: ${it.status}`}
                  >
                    {STAGES.map((s, i) => (
                      <span
                        key={s}
                        className={`rn-stagebar-seg seg-${idx} ${
                          i <= idx ? "is-reached" : ""
                        }`}
                        title={s}
                      />
                    ))}
                  </div>

                  {it.eta ? (
                    <span className="rn-progress-eta">Target: {it.eta}</span>
                  ) : null}

                  {/* ── Owner edit row ── */}
                  {isAdmin && isEditing && (
                    <div className="rn-admin-row">
                      <label className="rn-admin-field">
                        Stage
                        <select
                          value={draft.status}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, status: e.target.value }))
                          }
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="rn-admin-field rn-admin-field--eta">
                        Target
                        <input
                          type="text"
                          placeholder="e.g. Sep 2026"
                          maxLength={100}
                          value={draft.eta}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, eta: e.target.value }))
                          }
                        />
                      </label>
                      <div className="rn-admin-actions">
                        <button
                          type="button"
                          className="rn-admin-save"
                          onClick={() => saveEdit(it)}
                          disabled={savingId === it.id}
                        >
                          {savingId === it.id ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="rn-admin-cancel"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {error ? <div className="rn-request-error">{error}</div> : null}
        </div>
      </div>
    </div>
  );
};

export default ProgressDashboardModal;
